import React from "react";

/**
 * HeroFrame
 * Renders a circular, “golden painting” frame with an image inside.
 * Props:
 *  - src: image URL
 *  - alt: string
 *  - className: optional extra class
 *  - style: optional outer wrapper styles
 *  - children: optional custom media/content rendered inside the circular frame
 */
const HeroFrame = ({ src, alt = "", className = "", style = {}, children = null }) => {
  return (
    <div
      className={className}
      style={{
        position: "relative",
        display: "grid",
        placeItems: "center",
        aspectRatio: "1 / 1",
        // outer soft glow (subtle)
        filter: "drop-shadow(0 18px 40px rgba(0,0,0,.45))",
        ...style,
      }}
    >
      {/* Outer gold ring */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
            background:
              "conic-gradient(#e0b84f, #cfa53e 30deg, #f6d774 120deg, #c4962d 210deg, #e0b84f 360deg)",
            padding: "18px",
            boxSizing: "border-box",
        }}
      >
        {/* Inner black ring */}
        <div
          style={{
            borderRadius: "9999px",
            background:
              "radial-gradient(60% 60% at 50% 50%, #0c0d11 0%, #0a0b0f 60%, #000 100%)",
            padding: "12px",
            position: "relative",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Rivet lights */}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const r = 0.9; // radius in container space
            const x = 50 + Math.cos(angle) * r * 50;
            const y = 50 + Math.sin(angle) * r * 50;
            return (
              <span
                key={i}
                style={{
                  position: "absolute",
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: "translate(-50%, -50%)",
                  width: 10,
                  height: 10,
                  borderRadius: "9999px",
                  background:
                    "radial-gradient(circle at 30% 30%, #ffd66b, #e4b337 55%, #8b6b1b 100%)",
                  boxShadow: "0 0 10px rgba(255,214,107,.6)",
                }}
              />
            );
          })}

          {/* Image circle */}
          <div
            style={{
              position: "relative",
              borderRadius: "9999px",
              overflow: "hidden",
              // a light canvas/linen vibe
              background:
                "repeating-linear-gradient(0deg, rgba(255,255,255,.03) 0 2px, rgba(255,255,255,.02) 2px 4px)",
              width: "100%",
              height: "100%",
            }}
          >
            {children || (src ? (
              <img
                src={src}
                alt={alt}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : null)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroFrame;
